import { z } from 'zod';
import { PdfStatusSchema } from './deposits.js';
import { ReceiptSchema } from './receipts.js';
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
// ───────────────────────────── Petty cash (קופה קטנה) ───────────────────────
//
// One petty cash per (box, project). It's loaded from cash receipts (like a
// deposit) and expenses are documented out of it. No stored balance:
//   balance = opening_balance + Σ active loads − Σ active expenses.
export const PettyCashSchema = z.object({
    id: z.number(),
    project_id: z.number(),
    name: z.string(),
    opening_balance: z.number(),
});
export const CreatePettyCashRequestSchema = z.object({
    /** Ignored unless the box manages projects. */
    project_id: z.number().optional(),
    name: z.string().trim().min(1).max(120),
    opening_balance: z.number().min(0),
});
/** Name is always editable. The opening balance only while the petty cash has
 *  no active movements — after that it's part of the history. */
export const UpdatePettyCashRequestSchema = z.object({
    name: z.string().trim().min(1).max(120),
    opening_balance: z.number().min(0),
});
/** The petty cash screen header. */
export const PettyCashStateSchema = z.object({
    petty_cash: PettyCashSchema,
    balance: z.number(),
    /** Whether the opening balance can still be changed (no active movements). */
    opening_balance_editable: z.boolean(),
    /** Free cash receipts that can be loaded right now. */
    available_cash_total: z.number(),
    available_receipt_count: z.number(),
    /** Current calendar month (Israel time), active movements only. */
    month_loaded: z.number(),
    month_spent: z.number(),
});
// ─────────────────────────────── Categories ─────────────────────────────────
export const PettyCashCategorySchema = z.object({
    id: z.number(),
    name: z.string(),
    is_active: z.boolean(),
});
export const UpsertPettyCashCategoryRequestSchema = z.object({
    name: z.string().trim().min(1).max(80),
    is_active: z.boolean().optional(),
});
// ─────────────────────────────── Movements ──────────────────────────────────
export const PettyCashMovementTypeSchema = z.enum(['load', 'expense']);
export const PettyCashMovementSchema = z.object({
    id: z.number(),
    movement_number: z.number(),
    movement_type: PettyCashMovementTypeSchema,
    movement_date: z.string(),
    amount: z.number(),
    currency: z.string(),
    category_id: z.number().nullable(),
    category_name: z.string().nullable(),
    description: z.string().nullable(),
    notes: z.string().nullable(),
    status: z.enum(['active', 'cancelled']),
    /** Loads only: how many receipts were loaded. */
    receipt_count: z.number(),
    pdf_status: PdfStatusSchema,
    created_by_user_boxes_id: z.number().nullable(),
    created_by_name: z.string().nullable(),
    created_at: z.string(),
    cancelled_at: z.string().nullable(),
    cancelled_by_name: z.string().nullable(),
    cancel_reason: z.string().nullable(),
});
export const ListPettyCashMovementsQuerySchema = z.object({
    from: z.string().regex(ISO_DATE),
    to: z.string().regex(ISO_DATE),
});
/** Free cash receipts that can be loaded into the petty cash. */
export const PettyCashAvailableSchema = z.object({
    total: z.number(),
    receipts: z.array(ReceiptSchema),
});
export const CreatePettyCashLoadRequestSchema = z.object({
    movement_date: z.string().regex(ISO_DATE),
    /** How much to load. The server drains the oldest free cash receipts in
     *  order; the last one it touches may be taken in part. */
    amount: z.number().positive(),
    notes: z.string().max(1000).nullable().optional(),
});
export const CreatePettyCashExpenseRequestSchema = z.object({
    movement_date: z.string().regex(ISO_DATE),
    amount: z.number().positive(),
    category_id: z.number(),
    description: z.string().trim().min(1).max(500),
    notes: z.string().max(1000).nullable().optional(),
});
export const CancelPettyCashMovementRequestSchema = z.object({
    reason: z.string().trim().min(1).max(500),
});
export const PettyCashLoadReceiptSchema = z.object({
    id: z.number(),
    external_receipt_id: z.string(),
    receipt_number: z.string(),
    customer_name: z.string(),
    /** The part of the receipt this load took. */
    amount: z.number(),
    /** The receipt's full amount (> amount when the load took only part). */
    receipt_amount: z.number(),
    issue_date: z.string(),
});
//# sourceMappingURL=petty-cash.js.map